import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const extractDomain = (email, website) => {
  if (email) {
    const d = email.split('@')[1];
    if (d) return d.toLowerCase();
  }
  if (website) return website.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase();
  return null;
};

const formatDate = (val) => {
  if (!val) return null;
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatLocation = (city, state) => {
  if (city && state) return `${city}, ${state}`;
  return city || state || null;
};

// Globe icon for domains
const GlobeIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

// Calendar icon
const CalIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-zinc-400">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Folder icon
const FolderIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
    <path d="M2 4.5A1.5 1.5 0 013.5 3h3l2 2h4A1.5 1.5 0 0114 6.5v5a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 012 11.5z" />
  </svg>
);

// Settings/gear icon for View button
const SettingsIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
    <path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const VendorEcosystemView = ({ loaded }) => {
  const [vendors, setVendors] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeFolder, setActiveFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [invoiceFilter, setInvoiceFilter] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dropdown states
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [folderContextMenu, setFolderContextMenu] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [showFilterDd, setShowFilterDd] = useState(false);

  const addMenuRef = useRef(null);
  const folderPickerRef = useRef(null);
  const folderCtxRef = useRef(null);
  const filterDdRef = useRef(null);

  // -----------------------------------------------------------------------
  // Fetching
  // -----------------------------------------------------------------------

  const fetchEcosystem = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = activeFolder ? `/api/vendors/ecosystem?folder=${activeFolder}` : '/api/vendors/ecosystem';
      const res = await apiFetch(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch');
      setVendors(data.vendors || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeFolder]);

  const fetchFolders = async () => {
    try {
      const res = await apiFetch('/api/vendor-folders');
      const data = await res.json();
      if (res.ok) setFolders(data.folders || []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchFolders(); }, []);
  useEffect(() => { fetchEcosystem(); }, [fetchEcosystem]);
  useEffect(() => { setSelectedIds(new Set()); }, [activeFolder]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
      if (folderPickerRef.current && !folderPickerRef.current.contains(e.target)) setShowFolderPicker(false);
      if (folderCtxRef.current && !folderCtxRef.current.contains(e.target)) setFolderContextMenu(null);
      if (filterDdRef.current && !filterDdRef.current.contains(e.target)) setShowFilterDd(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // -----------------------------------------------------------------------
  // Folder actions
  // -----------------------------------------------------------------------

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await apiFetch('/api/vendor-folders', { method: 'POST', body: JSON.stringify({ name: newFolderName.trim() }) });
      if (res.ok) { setNewFolderName(''); setShowCreateFolder(false); fetchFolders(); }
    } catch { /* silent */ }
  };

  const handleRenameFolder = async (id) => {
    if (!renameValue.trim()) return;
    try {
      const res = await apiFetch(`/api/vendor-folders/${id}`, { method: 'PUT', body: JSON.stringify({ name: renameValue.trim() }) });
      if (res.ok) { setRenamingFolder(null); setRenameValue(''); fetchFolders(); }
    } catch { /* silent */ }
  };

  const handleDeleteFolder = async (id) => {
    try {
      const res = await apiFetch(`/api/vendor-folders/${id}`, { method: 'DELETE' });
      if (res.ok) { if (activeFolder === id) setActiveFolder(null); setFolderContextMenu(null); fetchFolders(); }
    } catch { /* silent */ }
  };

  const handleAddToFolder = async (folderId) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await apiFetch(`/api/vendor-folders/${folderId}/vendors`, { method: 'POST', body: JSON.stringify({ vendorIds: [...selectedIds] }) });
      if (res.ok) { setSelectedIds(new Set()); setShowFolderPicker(false); fetchFolders(); if (activeFolder) fetchEcosystem(); }
    } catch { /* silent */ }
  };

  const handleRemoveFromFolder = async () => {
    if (!activeFolder || selectedIds.size === 0) return;
    try {
      const res = await apiFetch(`/api/vendor-folders/${activeFolder}/vendors`, { method: 'DELETE', body: JSON.stringify({ vendorIds: [...selectedIds] }) });
      if (res.ok) { setSelectedIds(new Set()); fetchFolders(); fetchEcosystem(); }
    } catch { /* silent */ }
  };

  // -----------------------------------------------------------------------
  // Filtering
  // -----------------------------------------------------------------------

  const filtered = vendors.filter((v) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const haystack = [v.display_name, v.company_name, v.email].filter(Boolean);
      if (!haystack.some((s) => s.toLowerCase().includes(q))) return false;
    }
    if (statusFilter === 'on_arbcor' && !v.in_ecosystem) return false;
    if (statusFilter === 'not_on_arbcor' && v.in_ecosystem) return false;
    if (invoiceFilter === 'receiving' && !v.has_email_invoices) return false;
    if (invoiceFilter === 'not_receiving' && v.has_email_invoices) return false;
    return true;
  });

  //const allSelected = filtered.length > 0 && filtered.every((v) => selectedIds.has(v.id));
  //const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(filtered.map((v) => v.id)));
  const toggleSelect = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const hasFilters = statusFilter || invoiceFilter;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="max-w-[1400px] mx-auto">

      {/* ── Dark top bar ──────────────────────────────────────────────── */}
      <div
        className="flex items-center rounded-xl mb-5 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] relative z-30"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '300ms', backgroundColor: '#1c1c1c' }}
      >
        {/* Left: tabs */}
        <div className="flex items-center gap-1 px-2 py-2 overflow-x-auto min-w-0 flex-1">
          <button
            onClick={() => setActiveFolder(null)}
            className={`px-3.5 py-1.5 text-[12px] font-medium rounded-lg transition-all whitespace-nowrap ${
              activeFolder === null
                ? 'bg-white/10 text-white'
                : 'text-stone-400 hover:text-white hover:bg-white/5'
            }`}
          >
            All Vendors
          </button>

          {folders.map((f) => (
            <div key={f.id} className="relative">
              {renamingFolder === f.id ? (
                <form onSubmit={(e) => { e.preventDefault(); handleRenameFolder(f.id); }} className="inline-flex">
                  <input
                    autoFocus value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => setRenamingFolder(null)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setRenamingFolder(null); }}
                    className="px-3 py-1 text-[12px] bg-white/10 text-white border-0 rounded-lg w-36 outline-none placeholder-stone-500"
                    placeholder="Folder name"
                  />
                </form>
              ) : (
                <button
                  onClick={() => setActiveFolder(f.id)}
                  onContextMenu={(e) => { e.preventDefault(); setFolderContextMenu(f.id); }}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-medium rounded-lg transition-all whitespace-nowrap ${
                    activeFolder === f.id
                      ? 'bg-white/15 text-white'
                      : 'text-stone-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <FolderIcon size={11} />
                  {f.name}
                  <span className="text-[10px] text-stone-500">{f.vendor_count}</span>
                </button>
              )}
              {folderContextMenu === f.id && (
                <div ref={folderCtxRef} className="absolute top-full left-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-30 py-1 min-w-[120px]">
                  <button onClick={() => { setRenamingFolder(f.id); setRenameValue(f.name); setFolderContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-[12px] text-stone-700 hover:bg-stone-50">Rename</button>
                  <button onClick={() => handleDeleteFolder(f.id)} className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50">Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right: action buttons */}
        <div className="flex items-center gap-1.5 px-2 shrink-0" ref={addMenuRef}>
          {showCreateFolder ? (
            <form onSubmit={(e) => { e.preventDefault(); handleCreateFolder(); }} className="flex items-center gap-2 pr-1">
              <input
                autoFocus value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setShowCreateFolder(false); setNewFolderName(''); } }}
                placeholder="Folder name"
                className="px-3 py-1 text-[12px] bg-white/10 text-white border-0 rounded-lg w-32 outline-none placeholder-stone-500"
              />
              <button type="submit" className="px-2.5 py-1 text-[11px] bg-white text-stone-900 rounded-lg hover:bg-stone-100 font-medium">Create</button>
              <button type="button" onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }} className="text-[11px] text-stone-500 hover:text-white">Cancel</button>
            </form>
          ) : (
            <>
              <div className="relative">
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-white border border-stone-600 rounded-lg hover:bg-white/5 transition-colors"
                >
                  Add
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2v8M2 6h8" /></svg>
                </button>
                {showAddMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-30 py-1 min-w-[140px]">
                    <button
                      onClick={() => { setShowCreateFolder(true); setShowAddMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-[12px] text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                    >
                      <FolderIcon size={12} />
                      New Folder
                    </button>
                  </div>
                )}
              </div>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white border border-stone-600 rounded-lg hover:bg-white/5 transition-colors"
              >
                View
                <SettingsIcon />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-2.5 mb-4 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] relative z-20"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transitionDelay: '370ms' }}
      >
        <div className="relative flex-1 max-w-[240px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter Accounts By Name..."
            className="w-full pl-8 pr-3 py-1.5 text-[12px] text-stone-900 placeholder-stone-400 border border-stone-200 rounded-lg outline-none focus:border-stone-400 transition-colors"
          />
        </div>

        {/* Active filter pills — centered */}
        <div className="flex items-center gap-1.5 flex-1 justify-center">
          {statusFilter && (
            <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-medium bg-stone-100 text-stone-700 border border-stone-200">
              Arbcor Status
              <span className="text-stone-400 mx-0.5">·</span>
              {statusFilter === 'on_arbcor' ? 'On Arbcor' : 'Not on Arbcor'}
              <button onClick={() => setStatusFilter(null)} className="ml-0.5 rounded-full hover:bg-stone-200 p-0.5 transition-colors">
                <svg width="8" height="8" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="2"><path d="M2.5 2.5l5 5M7.5 2.5l-5 5" /></svg>
              </button>
            </span>
          )}
          {invoiceFilter && (
            <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-medium bg-stone-100 text-stone-700 border border-stone-200">
              Invoices
              <span className="text-stone-400 mx-0.5">·</span>
              {invoiceFilter === 'receiving' ? 'Receiving' : 'Not Receiving'}
              <button onClick={() => setInvoiceFilter(null)} className="ml-0.5 rounded-full hover:bg-stone-200 p-0.5 transition-colors">
                <svg width="8" height="8" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="2"><path d="M2.5 2.5l5 5M7.5 2.5l-5 5" /></svg>
              </button>
            </span>
          )}
        </div>

        {/* Filter button — right side */}
        <div className="relative shrink-0" ref={filterDdRef}>
          <button
            onClick={() => setShowFilterDd(!showFilterDd)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-lg border transition-colors ${
              hasFilters ? 'border-stone-400 text-stone-900 bg-stone-50' : 'border-stone-200 text-stone-500 hover:border-stone-300'
            }`}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filter
          </button>
          {showFilterDd && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-30 py-1 min-w-[180px]">
              <div className="px-3 py-1.5 text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Arbcor Status</div>
              <button onClick={() => { setStatusFilter('on_arbcor'); setShowFilterDd(false); }} className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-stone-50 flex items-center gap-2 ${statusFilter === 'on_arbcor' ? 'text-stone-900 font-medium' : 'text-stone-600'}`}>
                {statusFilter === 'on_arbcor' && <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />}
                On Arbcor
              </button>
              <button onClick={() => { setStatusFilter('not_on_arbcor'); setShowFilterDd(false); }} className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-stone-50 flex items-center gap-2 ${statusFilter === 'not_on_arbcor' ? 'text-stone-900 font-medium' : 'text-stone-600'}`}>
                {statusFilter === 'not_on_arbcor' && <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />}
                Not on Arbcor
              </button>
              <div className="border-t border-stone-100 my-1" />
              <div className="px-3 py-1.5 text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Email Invoices</div>
              <button onClick={() => { setInvoiceFilter('receiving'); setShowFilterDd(false); }} className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-stone-50 flex items-center gap-2 ${invoiceFilter === 'receiving' ? 'text-stone-900 font-medium' : 'text-stone-600'}`}>
                {invoiceFilter === 'receiving' && <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />}
                Receiving
              </button>
              <button onClick={() => { setInvoiceFilter('not_receiving'); setShowFilterDd(false); }} className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-stone-50 flex items-center gap-2 ${invoiceFilter === 'not_receiving' ? 'text-stone-900 font-medium' : 'text-stone-600'}`}>
                {invoiceFilter === 'not_receiving' && <span className="w-1.5 h-1.5 rounded-full bg-stone-900" />}
                Not Receiving
              </button>
              {hasFilters && (
                <>
                  <div className="border-t border-stone-100 my-1" />
                  <button onClick={() => { setStatusFilter(null); setInvoiceFilter(null); setShowFilterDd(false); }} className="w-full text-left px-3 py-1.5 text-[11px] text-stone-400 hover:bg-stone-50">
                    Clear all filters
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Bulk actions ───────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="mb-4 px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-lg flex items-center gap-3">
          <span className="text-[13px] text-zinc-700 font-medium">{selectedIds.size} selected</span>
          <div className="relative" ref={folderPickerRef}>
            <button onClick={() => setShowFolderPicker(!showFolderPicker)} className="px-3 py-1.5 text-[13px] border border-zinc-200 rounded-lg hover:bg-white transition-colors text-zinc-700">
              Add to Folder
            </button>
            {showFolderPicker && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-30 py-1 min-w-[160px]">
                {folders.length === 0 ? (
                  <p className="px-3 py-2 text-[13px] text-zinc-400">No folders yet</p>
                ) : folders.map((f) => (
                  <button key={f.id} onClick={() => handleAddToFolder(f.id)} className="w-full text-left px-3 py-2 text-[13px] text-zinc-700 hover:bg-zinc-50 flex items-center gap-2">
                    <FolderIcon size={13} />{f.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {activeFolder && (
            <button onClick={handleRemoveFromFolder} className="px-3 py-1.5 text-[13px] text-red-600 hover:bg-red-50 rounded-lg transition-colors">
              Remove from Folder
            </button>
          )}
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-[13px] text-zinc-400 hover:text-zinc-600">Clear</button>
        </div>
      )}

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <span className="text-[13px] text-red-600">{error}</span>
          <button onClick={() => { setError(null); fetchEcosystem(); }} className="ml-auto text-[12px] text-red-500 hover:text-red-700 font-medium">Retry</button>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div
        className="border border-zinc-200 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] relative z-10"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '440ms' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-24 bg-white">
            <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center bg-white">
            <p className="text-[13px] text-zinc-400">
              {vendors.length === 0
                ? activeFolder
                  ? 'This folder is empty. Select vendors and add them to this folder.'
                  : 'No vendors found. Sync your vendors from QuickBooks or add them manually.'
                : 'No vendors match your search or filters.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" style={{ minWidth: 860 }}>
                <thead>
                  <tr className="border-b border-zinc-200 bg-white">
                    <th className="sticky left-0 z-10 bg-white pl-3 pr-2 py-2 text-[11px] text-zinc-500 font-medium text-left border-r border-zinc-200" style={{ width: 200 }}>
                      Account
                    </th>
                    <th className="bg-stone-50/80 px-3 py-2 text-[11px] text-zinc-500 font-medium text-left" style={{ minWidth: 130 }}>Domain</th>
                    <th className="bg-stone-50/80 px-3 py-2 text-[11px] text-zinc-500 font-medium text-left" style={{ minWidth: 110 }}>Location</th>
                    <th className="bg-stone-50/80 px-3 py-2 text-[11px] text-zinc-500 font-medium text-left" style={{ minWidth: 110 }}>Arbcor Status</th>
                    <th className="bg-stone-50/80 px-3 py-2 text-[11px] text-zinc-500 font-medium text-left" style={{ minWidth: 120 }}>Last Interaction</th>
                    <th className="bg-stone-50/80 px-3 py-2 text-[11px] text-zinc-500 font-medium text-left" style={{ minWidth: 110 }}>Email Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v, i) => {
                    const domain = extractDomain(v.email, v.website);
                    const location = formatLocation(v.city, v.state);
                    const lastDate = formatDate(v.last_email_invoice_at);

                    return (
                      <tr
                        key={v.id}
                        onClick={() => toggleSelect(v.id)}
                        className="group border-b border-zinc-100 last:border-0 bg-white hover:bg-zinc-50/50 transition-colors cursor-pointer"
                        style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.4s cubic-bezier(0.16,1,0.3,1) ${400 + i * 15}ms` }}
                      >
                        <td className="sticky left-0 z-10 bg-white pl-3 pr-2 py-1.5 border-r border-zinc-200 group-hover:bg-zinc-50" style={{ width: 200 }}>
                          <span className="text-[12px] font-medium text-zinc-900 whitespace-nowrap truncate block">{v.display_name}</span>
                        </td>

                        <td className="bg-stone-50/80 px-3 py-1.5">
                          {domain ? (
                            <span className="inline-flex items-center gap-1 text-[12px] text-blue-600/80 whitespace-nowrap">
                              <GlobeIcon />
                              {domain}
                            </span>
                          ) : null}
                        </td>

                        <td className="bg-stone-50/80 px-3 py-1.5">
                          {location && <span className="text-[12px] text-zinc-500 whitespace-nowrap">{location}</span>}
                        </td>

                        <td className="bg-stone-50/80 px-3 py-1.5">
                          {v.in_ecosystem ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              On Arbcor
                            </span>
                          ) : null}
                        </td>

                        <td className="bg-stone-50/80 px-3 py-1.5">
                          {lastDate ? (
                            <span className="inline-flex items-center gap-1 text-[12px] text-zinc-500 whitespace-nowrap">
                              <CalIcon />
                              {lastDate}
                            </span>
                          ) : null}
                        </td>

                        <td className="bg-stone-50/80 px-3 py-1.5">
                          {v.has_email_invoices ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-orange-700 bg-orange-50 whitespace-nowrap">
                              {v.email_invoice_count} received
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50/40">
              <span className="text-[10px] text-zinc-400">
                {filtered.length} vendor{filtered.length !== 1 ? 's' : ''}
                {filtered.length !== vendors.length && ` of ${vendors.length}`}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VendorEcosystemView;
